import React from 'react';
import { Warning } from '@mui/icons-material';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  styled,
  Stack,
  Typography,
  Link,
} from '@mui/material';
import { Button } from 'components/Button/Button';
import { CloseButton } from 'components/CloseButton/CloseButton';
import { SvgIcon } from 'components/SvgIcon/SvgIcon';
import { useLayoutElements } from 'contexts/LayoutElementsContext/useLayoutElements';

/** Represents a help element rendered as a hyperlink. */
interface ErrorDialogHelpLinkProps {
  /** The type of the help element. */
  type: 'link';
  /** The visible text for the link. */
  displayText: string;
  /** The link URL. */
  url: string;
}

/** Represents a dialog action button. */
interface ErrorDialogButtonProps {
  /** The button label. */
  label: string;
  /** The button click handler. */
  onClick: () => void;
}

/** Represents a help element rendered as a button. */
interface ErrorDialogHelpButtonProps extends ErrorDialogButtonProps {
  /** The type of the help element. */
  type: 'button';
}

export interface ErrorDialogProps {
  open: boolean;
  title: string;
  body: string;
  helpElement?: ErrorDialogHelpButtonProps | ErrorDialogHelpLinkProps;
  primaryCtaButton: ErrorDialogButtonProps;
  secondaryCtaButton?: ErrorDialogButtonProps;
  onClose: () => void;
}

const StyledDialogActions = styled(DialogActions)(({ theme }) => ({
  flexDirection: 'column',
  alignItems: 'normal',
  gap: theme.spacing(2),
  padding: theme.spacing(4),
}));

export const ErrorDialog = ({
  open,
  title,
  body,
  helpElement,
  primaryCtaButton,
  secondaryCtaButton,
  onClose,
}: ErrorDialogProps) => {
  const { isAlertOpen } = useLayoutElements();

  const handleButtonClick = (onClick: () => void) => {
    onClick();
    onClose();
  };

  return (
    <Dialog
      className="error-dialog"
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      aria-labelledby="error-dialog__title"
      aria-describedby="error-dialog__body"
      aria-modal="true"
      disableEscapeKeyDown={isAlertOpen} // Prevent closing if an alert is open
      disableEnforceFocus // Allow TalkBack to navigate freely
      PaperProps={{
        tabIndex: 0,
      }}
    >
      <DialogContent sx={{ padding: 0 }}>
        <Box display="flex" flexDirection="column" paddingTop={1} paddingX={1}>
          <CloseButton
            className="error-dialog__close_button"
            onClick={onClose}
            sx={{ alignSelf: 'flex-end' }}
            aria-label="Close error dialog"
          />
          <Stack spacing={2} alignItems="center" textAlign="center" paddingX={4}>
            <SvgIcon
              className="error-dialog__icon"
              component={Warning}
              style={{ color: '#F15961' }}
              fontSize="large"
              aria-hidden="true"
            />
            <Typography
              className="error-dialog__title"
              variant="title3"
              color="primary"
              id="error-dialog__title"
            >
              {title}
            </Typography>
            <Typography className="error-dialog__body" variant="body1" id="error-dialog__body">
              {body}
            </Typography>
            {helpElement?.type === 'button' && (
              <Button
                className="error-dialog__help-button"
                variant="text"
                onClick={() => handleButtonClick(helpElement.onClick)}
              >
                <Typography variant="body1">{helpElement.label}</Typography>
              </Button>
            )}
            {helpElement?.type === 'link' && (
              <Link
                className="error-dialog__help-link"
                variant="body1"
                color="primary"
                underline="none"
                alignSelf="center"
                target="_blank"
                rel="noopener noreferrer"
                href={helpElement.url}
              >
                {helpElement.displayText}
              </Link>
            )}
          </Stack>
        </Box>
      </DialogContent>
      <StyledDialogActions disableSpacing>
        <Button
          className="error-dialog__primary-button"
          variant="primary"
          onClick={() => handleButtonClick(primaryCtaButton.onClick)}
        >
          {primaryCtaButton.label}
        </Button>
        {secondaryCtaButton && (
          <Button
            className="error-dialog__secondary-button"
            variant="outlined"
            onClick={() => handleButtonClick(secondaryCtaButton.onClick)}
          >
            {secondaryCtaButton.label}
          </Button>
        )}
      </StyledDialogActions>
    </Dialog>
  );
};
